import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPrint, faSave, faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import { StatusBadge } from '../shared/StatusBadge';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import SymbolBar from '../../components/product/SymbolBar';
import {
    ProductHeaderWrapper,
    NavigationBar,
    ProductTitleSection,
    ProductTitleRow,
    TitleLeft,
    TitleRight,
    BackButton,
    PrintButton,
    ProductHeadingTitle,
    ProductSymbolsRow,
    ButtonGroup,
} from './ProductHeader.styles';
import { SaveButton, CancelButton, CopyButton } from './EditableFields.styles';
import { ProductDetail } from '../../types/ProductTypes';
import { formatDateTime } from '../../utils/dateFormatters';

interface ProductHeaderProps {
    title: string;
    subtitle?: React.ReactNode;
    isActive?: boolean;
    product: ProductDetail;
    onBackClick: () => void;
    backButtonTitle?: string;
    showPrintButton?: boolean;
    showStatusBadge?: boolean;
    showEditButtons?: boolean;
    showCopyButton?: boolean;
    onSave?: () => Promise<void>;
    onCancel?: () => void;
    onCopy?: () => void;
    isSaving?: boolean;
    hasUnsavedChanges?: boolean;
    children?: React.ReactNode;
}

const ProductHeader: React.FC<ProductHeaderProps> = ({
    title,
    subtitle,
    isActive = true,
    product,
    onBackClick,
    showPrintButton = true,
    showStatusBadge = true,
    showEditButtons = false,
    showCopyButton = false,
    onSave,
    onCancel,
    onCopy,
    isSaving = false,
    hasUnsavedChanges = false,
    children,
}) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const showEditUI = canEdit && isInEditMode && showEditButtons;

    return (
        <ProductHeaderWrapper>
            {/* Edit action buttons at the top */}
            {showEditUI && (
                <NavigationBar style={{ justifyContent: 'flex-end' }}>
                    <ButtonGroup>
                        {showCopyButton && onCopy && (
                            <CopyButton
                                onClick={onCopy}
                                disabled={hasUnsavedChanges}
                                title={hasUnsavedChanges ? 'Save or discard changes before copying' : 'Copy Charterer Specific Product / Trade Name'}
                            >
                                <FontAwesomeIcon icon={faCopy} style={{ marginRight: '5px' }} />
                                Copy Trade Name
                            </CopyButton>
                        )}
                        {onCancel && onSave && (
                            <>
                                <CancelButton onClick={onCancel} disabled={isSaving}>
                                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                                    Cancel
                                </CancelButton>
                                <SaveButton onClick={onSave} disabled={isSaving}>
                                    <FontAwesomeIcon icon={faSave} style={{ marginRight: '5px' }} />
                                    {isSaving ? 'Saving...' : 'Save'}
                                </SaveButton>
                            </>
                        )}
                    </ButtonGroup>
                </NavigationBar>
            )}

            {/* Back and Print buttons */}
            <NavigationBar>
                <BackButton onClick={onBackClick}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                </BackButton>
                {showPrintButton && (
                    <PrintButton onClick={() => window.print()}>
                        <FontAwesomeIcon icon={faPrint} /> Print
                    </PrintButton>
                )}
            </NavigationBar>

            {/* Product title and symbols */}
            <ProductTitleSection>
                <ProductTitleRow>
                    <TitleLeft>
                        <ProductHeadingTitle style={{ fontSize: title.length > 40 ? '1.5rem' : '2.25rem' }}>
                            {title}
                            {subtitle}
                        </ProductHeadingTitle>
                    </TitleLeft>
                    <TitleRight>
                        {showStatusBadge && <StatusBadge variant={isActive ? 'active' : 'inactive'}>{isActive ? 'Active' : 'Inactive'}</StatusBadge>}
                        <SymbolBar product={product} canEdit={false} />
                    </TitleRight>
                </ProductTitleRow>
                <ProductSymbolsRow>
                    <span style={{ fontSize: '0.85em', color: '#666' }}>Last updated: {formatDateTime(product.lastUpdatedUtc)}</span>
                    {children}
                </ProductSymbolsRow>
            </ProductTitleSection>
        </ProductHeaderWrapper>
    );
};

export default ProductHeader;
